<template>
  <RevDocument
    :error-message
    :name="attachment.name"
    :status
    :url="attachment.url"
  />
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { RevDocument } from '@ds/components/Document'

import type { Attachment } from '~/api/seller-after-sales/types'

import translations from './Attachment.translations'

const logger = useLogger()
const t = useI18n()

const { attachment } = defineProps<{
  attachment: Attachment
}>()

const errorMessage = ref<string | undefined>()
const expirationTimerId = ref<ReturnType<typeof setTimeout> | undefined>()

const status = computed(() => {
  if (errorMessage.value) return 'error'

  return 'default'
})

function clearExistingTimer(): void {
  if (expirationTimerId.value) {
    clearTimeout(expirationTimerId.value)
    expirationTimerId.value = undefined
  }
}

function getExpirationParam(url: string): string | null {
  try {
    const parsedUrl = new URL(url)
    const expiresParam = parsedUrl.searchParams.get('expires')

    if (!expiresParam) {
      logger.error(
        `The attachment URL is missing the 'expires' parameter: ${url}`,
        { owners: ['bot-squad-seller-care-front'] },
      )
    }

    return expiresParam
  } catch (error) {
    logger.error(
      `Failed to parse the attachment URL: ${url}. Error: ${error}`,
      { owners: ['bot-squad-seller-care-front'] },
    )

    return null
  }
}

function calculateTimeUntilExpiration(expiresParam: string): number {
  // Convert the expiration parameter (in secs) to milliseconds
  const expirationTime = parseInt(expiresParam, 10) * 1000
  const currentTime = Date.now()

  return expirationTime - currentTime
}

function handleExpirationTiming(timeUntilExpiration: number): void {
  if (timeUntilExpiration > 0) {
    expirationTimerId.value = setTimeout(() => {
      errorMessage.value = t(translations.linkExpired)
      expirationTimerId.value = undefined
    }, timeUntilExpiration)
  } else {
    // If already expired, show the error immediately
    errorMessage.value = t(translations.linkExpired)
  }
}

function handleExpiration(): void {
  clearExistingTimer()

  const expiresParam = getExpirationParam(attachment.url)

  if (expiresParam) {
    const timeUntilExpiration = calculateTimeUntilExpiration(expiresParam)

    logger.info(
      `Attachment url will expire in ~${Math.round(timeUntilExpiration / (60 * 1000))}mins. Timestamp: ${expiresParam}`,
      { owners: ['bot-squad-seller-care-front'] },
    )
    handleExpirationTiming(timeUntilExpiration)
  }
}

watch(() => attachment, handleExpiration, { immediate: true })

onUnmounted(() => {
  // Clean up the timer when component is destroyed
  if (expirationTimerId.value) {
    clearTimeout(expirationTimerId.value)
  }
})
</script>
